// Deliberately NOT marked `'use client'`, even though the hooks below are client-only.
//
// The directive applies to the whole module, so it would turn every export into a client reference
// — including `getCachedContentData`, which is awaited from server components (page `metadata`,
// `sitemap.ts`, the cinema-website's server sections). Those calls fail at runtime with
// "Attempted to call getCachedContentData() from the server but getCachedContentData is on the
// client". The hooks need no directive of their own: every caller is a client component that
// carries one, which is what puts this module in the client graph. Same arrangement as the sibling
// `useCachedShowingData`, `useCachedMovieData` and `useContent` modules.
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import { GenreDto } from '@cineamo/api/models/movie/GenreDto.types';
import { MovieAccessibilityDto } from '@cineamo/api/models/movie/MovieAccessibilityDto.types';
import { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { CountryCode } from '@cineamo/api/types/CountryCode';

import { CACHE_12H } from '@cineamo/api/constants/caching-constants';
import { EventRequestCategory } from '@cineamo/api/constants/EventRequestCategory.enum';

import { getLocalizedContentDescription } from '@cineamo/api/helpers/content-helper';
import {
    getCrewMembersByJob,
    getMovieAccessibility,
    getMovieAgeRating,
    MovieVersionDto
} from '@cineamo/api/helpers/movie-helper';
import { getContentById } from '@cineamo/api/resources/content/content-api';
import { getMovieById } from '@cineamo/api/resources/movie/movie-v2-api';

import { getShowingDuration, getShowingName } from '@cineamo/ui/src/helpers/showing-helper';

import { getCachedMovieData } from '../useCachedMovieData';

import { getCachedShowingData } from './useCachedShowingData';
import { contentByIdQueryOptions } from './useContent';

/**
 * `include` for a detail surface: every relation the v2 movie endpoint keeps behind `?include=`.
 * A list surface should ask for {@link MOVIE_INCLUDE_LIST} instead and keep the payload small.
 */
export const MOVIE_INCLUDE_DETAIL =
    'videos,credits,images,translations,releaseDates,productionCompanies,productionCountries';

/**
 * `include` for list/card surfaces: only the release certifications, which an app needs to resolve
 * the age rating. Titles, overviews and the poster/backdrop paths are scalars the v2 endpoint always
 * returns already localized, so a list needs nothing else.
 */
export const MOVIE_INCLUDE_LIST = 'releaseDates';

/**
 * Everything a content (movie wrapper) renders, already resolved against the page locale: no DTO
 * reaches a component, and no component picks locale-dependent text itself.
 *
 * Same size rule as `CachedMovieData` and `CachedShowingData`, and it matters most here: the program
 * page holds one of these per row and the server serializes them into the RSC payload, so this stays
 * a small, flat set of primitives. It carries neither the `ContentDto` nor the `MovieDto` it was
 * derived from, and no nested cached object. A surface that needs one more derived value gets one
 * more named primitive field here — not a map of every variant, and not the source DTO.
 *
 * `genres`, `movieAccessibility` and `actors` are the only non-primitive members: each is rendered
 * directly and has no primitive equivalent. A fourth one needs a real justification.
 *
 * Shaped so it can also be passed straight to `ContentTitle` (`ContentTitleMovie`) and `GretaLabel`
 * (`MovieWithAccessibility`) — those read `releaseYear`/`runtime`/`genres`/`movieAccessibility`
 * structurally, so this flattened view fits them without a mapping step.
 *
 * Only `id`, the flags and the lists are guaranteed. Everything else is *derived* from a movie, a
 * showing, an event request or a custom-content entry, any of which may be absent for a given
 * content — and the images are all resolved with `usePlaceholder: false`, so "no artwork" is a real
 * answer rather than a placeholder.
 */
export type CachedContentData = {
    id: ContentDto['id'];
    /** Undefined for a content that carries no movie, and while the movie is still resolving. */
    movieId: MovieDto['id'] | undefined;
    hasCustomContent: boolean;
    hasMovie: boolean;
    /**
     * `false` while the content or its movie is still being fetched — everything derived from the
     * movie (title, overview, images, certification, popularity) is provisional until it flips to
     * `true`. A list that sorts by a movie field should wait for it rather than re-order under the
     * viewer. Always `true` for the awaited `getCachedContentData`, which resolves before returning.
     */
    isResolved: boolean;
    trailerUrl: ContentDto['trailerUrl'];
    description?: ContentDto['description'];
    name?: ContentDto['name'];
    title?: string;
    /** Slug of the CONTENT. For the movie's own slug see `movieSlug`. */
    slug?: ContentDto['slug'];
    /** Slug of the movie, for linking to its detail page. */
    movieSlug?: string;
    /** `null` when the caller passed `overrideReleaseDate: false` to suppress it entirely. */
    releaseDate?: string | null;
    releaseYear?: string;
    runtime?: number;
    genres: GenreDto[];
    /**
     * The age rating, resolved as far as this module can: the TMDB release certification for the
     * requested country, then whatever `resolveRegionAgeRating` returned, then the content's own.
     * An app that runs its region lookup itself (in a hook, off a movie id) gets only the TMDB
     * certification here and finishes the chain with `contentAgeRating`.
     */
    ageRating?: string;
    /**
     * The TMDB release certification alone, or `undefined` when TMDB has none for the country.
     * An app that runs the region lookup itself reads this instead of `ageRating` — the latter has
     * already fallen back to `contentAgeRating`, which would skip the lookup whenever the content
     * carries a rating of its own.
     */
    tmdbAgeRating?: string;
    /** The content's own rating — the last fallback of the chain above, exposed for that purpose. */
    contentAgeRating?: string;
    /** Fully resolved artwork: the movie's, then the showing's, then the content's, then a fallback. */
    backdropImage?: string;
    backdropImageURL_ORIGINAL?: string;
    backdropImageURL_W1280?: string;
    backdropImageURL_W780?: string;
    backdropBlurDataImage?: string;
    posterImage?: string;
    posterImageURL_W780?: string;
    posterBlurDataImage?: string;
    /** `null` whenever the displayed title is not the plain movie title — see the derivation. */
    logoImage?: string | null;
    showPoster: boolean;
    eventRequestCategory?: EventRequestCategory;
    movieAccessibility: MovieAccessibilityDto | null;
    director: string;
    actors: string[];
    originalTitle: MovieDto['originalTitle'];
    tmdbId: MovieDto['tmdbId'] | undefined;
    imdbId: MovieDto['imdbId'];
    /** Trailer flattened to the fields surfaces render — see `CachedMovieData`. */
    trailerName?: string;
    trailerThumbnailUrl?: string;
    trailerPublishedAt?: string;
    /** Drives "most popular first" sorting; 0 until the movie is resolved. */
    popularity: number;
};

/** Per-content overrides an app maintains outside the API (the cinema-website's custom contents). */
export type CustomContentOverrides = {
    title?: string;
    description?: string;
    posterUrl?: string;
    backdropUrl?: string;
};

/** The event request behind a non-regular showing, as this module reads it. */
export type CachedContentEventRequest = {
    name?: string;
    description?: string;
    category?: EventRequestCategory;
};

/**
 * The app-owned behaviour of the cached content data. Everything here is optional: omit it all and
 * you get the plain content + locale-aware movie resolution the multi-cinema web app wants.
 */
export type CachedContentDataAdapters = {
    /** Per-content overrides that win over the API's own title/description/images. */
    getCustomContent?: (contentId: ContentDto['id']) => CustomContentOverrides | undefined | null;
    /** Category-specific placeholder artwork for event requests. */
    getCategoryImage?: (category: EventRequestCategory, type: 'poster' | 'backdrop') => string;
    /** Placeholder artwork when there is neither movie, content nor category image. */
    fallbackPosterImage?: string;
    fallbackBackdropImage?: string;
    /** Loads the event request of a non-regular showing (its name/description/category win). */
    fetchEventRequest?: (eventRequestId: number | string) => Promise<CachedContentEventRequest | null>;
    /**
     * Region-specific rating lookup, used only when TMDB has no certification for the country.
     * An app whose surface resolves this in a hook instead leaves it out.
     */
    resolveRegionAgeRating?: (movieId: MovieDto['id'], cinema?: CinemaDto) => Promise<string | undefined>;
    /**
     * Which title wins when a showing carries its own name.
     *
     * `'movie-first'` (default) runs the title through `getShowingName`, so the movie's localized
     * title wins and the showing's name is only a last resort — what a multi-cinema listing wants,
     * where a row stands for the film.
     *
     * `'showing-first'` puts `showing.name` above everything: on a single cinema's site the name it
     * gave that particular showing ("Sneak Preview", "OmU-Fassung") IS the headline.
     */
    titleResolution?: 'movie-first' | 'showing-first';
    /** `include` sent to `/v2/movies/{id}`; defaults to {@link MOVIE_INCLUDE_DETAIL} when awaited. */
    movieInclude?: string;
};

export type CachedContentDataOptions = {
    /** Page locale — sent to `/v2/movies/{id}` as `Accept-Language`. */
    locale: string;
    /** Cinema context: its customization overrides the movie, its country the certification. */
    cinema?: CinemaDto;
    /**
     * Country the release certification is read for, when it should not be the cinema's. A row that
     * groups the showings of cinemas in several countries wants the URL locale's country instead.
     */
    countryCode?: CountryCode;
    overrideReleaseDate?: string | false;
} & CachedContentDataAdapters;

export type CachedContentDataInput = {
    /** `undefined` disables the input entirely (nothing is fetched, nothing is returned). */
    contentId?: ContentDto['id'];
    /**
     * The content, when the caller already holds it — a list that fetched its contents in one batch
     * passes them straight in. Omit it to have it fetched from the `contentByIdQueryOptions` cache
     * entry `useContent`/`useContentsByIds` share, so nothing is requested twice.
     */
    content?: ContentDto | null;
    /**
     * Showing this content is being rendered for, if any. Supplies the fallbacks (event-request
     * name, the showing's own name and images) — the movie always wins when there is one.
     */
    showing?: ShowingDto;
};

/** An input that actually names a content — the only kind the derivation and the map can key on. */
type IdentifiedContentDataInput = CachedContentDataInput & { contentId: ContentDto['id'] };

function isIdentified(input: CachedContentDataInput): input is IdentifiedContentDataInput {
    return !!input.contentId;
}

type DerivationInput = CachedContentDataInput & {
    /**
     * Required here, unlike on the public input: the id is the identity of the result, and an input
     * without one is filtered out before it ever reaches the derivation.
     */
    contentId: ContentDto['id'];
    movie?: MovieVersionDto | null;
    eventRequest?: CachedContentEventRequest | null;
    regionAgeRating?: string;
};

/**
 * Pure derivation, so it can run in a server component that already holds the data, inside a memo,
 * or after the awaited fetches of {@link getCachedContentData}.
 */
export function deriveCachedContentData(input: DerivationInput, options: CachedContentDataOptions): CachedContentData {
    const { content, showing, movie, eventRequest } = input;
    const { locale } = options;
    const countryCode = options.countryCode ?? (options.cinema?.countryCode as CountryCode | undefined);

    const movieData = movie ? getCachedMovieData({ movie, locale, countryCode }) : undefined;
    const showingData = showing ? getCachedShowingData(showing, movie, countryCode, locale) : undefined;
    const customContent = options.getCustomContent?.(input.contentId) ?? undefined;

    // A content with a `movieId` but no movie yet is still resolving; one without will never have a
    // movie (own/custom content), which is what tells "loading" apart from "has no movie".
    const isMovieResolved = !content?.movieId || !!movie;

    const showingTitle = showing ? getShowingName(showing, movie, locale, countryCode) : undefined;
    const title =
        options.titleResolution === 'showing-first'
            ? showing?.name ||
              eventRequest?.name ||
              customContent?.title ||
              movieData?.title ||
              content?.name ||
              content?.slug
            : customContent?.title || showingTitle || movieData?.title || content?.name || content?.slug;

    // Movie overview first (the backend already localized it for `locale`), then the app's override
    // and the event request, then the content's own description. While the movie is still resolving
    // nothing is returned rather than the content's default-locale text.
    const description = isMovieResolved
        ? customContent?.description ||
          eventRequest?.description ||
          movieData?.overview ||
          getLocalizedContentDescription(content ?? undefined, locale)
        : undefined;

    const category = eventRequest?.category;
    const fallbackBackdropImage = category
        ? options.getCategoryImage?.(category, 'backdrop')
        : options.fallbackBackdropImage;
    const fallbackPosterImage = category ? options.getCategoryImage?.(category, 'poster') : options.fallbackPosterImage;

    const customBackdropImage =
        customContent?.backdropUrl ||
        showingData?.backdropImageURL_W780 ||
        showingData?.posterImageURL_W780 ||
        content?.backdropImageUrl;
    const customPosterImage =
        customContent?.posterUrl ||
        showingData?.posterImageURL_W780 ||
        showingData?.backdropImageURL_W780 ||
        content?.posterImageUrl;

    const moviePosterImage = movieData?.posterImageURL_W780;

    return {
        id: input.contentId,
        movieId: movie?.id ?? content?.movieId,
        hasCustomContent: !!customContent,
        hasMovie: !!movie,
        isResolved: !!content && isMovieResolved,
        name: content?.name,
        title,
        slug: content?.slug,
        movieSlug: movie?.slug,
        description,
        // movie.trailer already resolves the preferred trailer server-side (v2 movie endpoint):
        // cinema customization > locale-matched KinoCheck trailer > TMDB videos
        trailerUrl: movieData?.trailerUrl ?? content?.trailerUrl,
        trailerName: movieData?.trailerName,
        trailerThumbnailUrl: movieData?.trailerThumbnailUrl,
        trailerPublishedAt: movieData?.trailerPublishedAt,
        releaseDate:
            options.overrideReleaseDate === false ? null : options.overrideReleaseDate || movieData?.releaseDate,
        releaseYear: movieData?.releaseYear,
        ageRating: movieData?.ageRating || input.regionAgeRating || content?.ageRating,
        tmdbAgeRating: movieData?.ageRating,
        contentAgeRating: content?.ageRating,
        runtime: movieData?.runtime || getShowingDuration(showing, movie ?? undefined, locale) || content?.duration,
        genres: movieData?.genres ?? [],
        backdropImageURL_ORIGINAL: movieData?.backdropImageURL_ORIGINAL,
        backdropImageURL_W1280: movieData?.backdropImageURL_W1280,
        backdropImageURL_W780: movieData?.backdropImageURL_W780,
        backdropImage: movieData?.backdropImageURL_W780 || customBackdropImage || fallbackBackdropImage,
        backdropBlurDataImage:
            customContent?.backdropUrl ||
            showingData?.backdropBlurDataImage ||
            showingData?.posterBlurDataImage ||
            options.fallbackBackdropImage,
        posterImageURL_W780: customContent?.posterUrl || moviePosterImage || options.fallbackPosterImage,
        posterImage: moviePosterImage || customPosterImage || content?.posterImageUrl || fallbackPosterImage,
        posterBlurDataImage: movieData?.posterBlurDataImage,
        // The logo is a title treatment of the ORIGINAL movie title, so only use it when the
        // displayed title IS that plain movie title — a custom/showing/event name must not be
        // overwritten by the logo (falls back to the title text downstream).
        logoImage: title === movieData?.title ? movieData?.logoImageURL_W780 : null,
        showPoster: !!(moviePosterImage || customPosterImage),
        eventRequestCategory: category,
        // via the helper: the API delivers the GRETA payload under `_embedded`, so reading
        // `movie.movieAccessibility` directly always yielded null (no GRETA badge ever showed)
        movieAccessibility: getMovieAccessibility(movie),
        director: getCrewMembersByJob(movie ?? undefined, 'Director')
            .map((crewMember) => crewMember.name)
            .join(', '),
        actors:
            movie?.credits?.cast
                ?.toSorted((a, b) => (a.order ?? 0) - (b.order ?? 0))
                ?.slice(0, 5)
                ?.map((castMember) => castMember.name)
                .filter((name): name is string => name !== undefined) ?? [],
        originalTitle: movie?.originalTitle,
        tmdbId: movie?.tmdbId,
        imdbId: movie?.imdbId,
        popularity: movie?.popularity ?? 0
    };
}

/**
 * Awaited resolution of a single content — for server components and anything outside React.
 *
 * The movie always comes from `/v2/movies/{id}` with the page locale as `Accept-Language`: it is the
 * only endpoint that answers with the text the backend picked for that locale. `/contents/{id}` and
 * the `cineamoMovie` it embeds are sent without the header and always carry the API's default-locale
 * text, so they are never a source for anything localized.
 */
export async function getCachedContentData(
    options: CachedContentDataOptions & { content: ContentDto; showing?: ShowingDto }
): Promise<CachedContentData> {
    // Re-fetched only when the caller's copy carries no `movieId`; if that request fails, keep the
    // caller's copy rather than losing the content altogether.
    const content = options.content.movieId
        ? options.content
        : ((await getContentById(options.content.id)) ?? options.content);
    const showing = options.showing;

    const movie = content?.movieId
        ? await getMovieById(content.movieId, {
              locale: options.locale,
              cinemaId: options.cinema?.id,
              include: options.movieInclude ?? MOVIE_INCLUDE_DETAIL
          })
        : null;

    const eventRequest =
        options.fetchEventRequest && !showing?.isRegularShowtime && showing?.eventRequestId
            ? await options.fetchEventRequest(showing.eventRequestId)
            : null;

    // Only worth a request when TMDB has no certification for the country.
    const countryCode = options.countryCode ?? (options.cinema?.countryCode as CountryCode | undefined);
    const regionAgeRating =
        movie && !getMovieAgeRating(movie, countryCode)
            ? await options.resolveRegionAgeRating?.(movie.id, options.cinema)
            : undefined;

    return deriveCachedContentData(
        { contentId: content.id, content, showing, movie, eventRequest, regionAgeRating },
        options
    );
}

function movieQueryOptions(movieId: MovieDto['id'], locale: string, cinemaId?: number, include?: string) {
    return {
        queryKey: ['cached-content-movie', movieId, locale, cinemaId, include],
        queryFn: () => getMovieById(movieId, { locale, cinemaId, include }),
        enabled: !!movieId,
        // Movie metadata rarely changes and the payload is locale-resolved server-side, so a long
        // window keeps a list of rows down to one request per movie. gcTime matches, otherwise
        // inactive entries are collected after the 5-minute default and the staleTime is moot.
        staleTime: CACHE_12H,
        gcTime: CACHE_12H,
        refetchOnWindowFocus: false
    };
}

/**
 * Resolves a batch of contents into `CachedContentData`, keyed by content id — the React equivalent
 * of {@link getCachedContentData} for a client-rendered list.
 *
 * One `/v2/movies/{id}` request per DISTINCT movie. The derivation itself is a memo, not a query per
 * row: it computes nothing that needs caching, and a per-row query entry with no staleTime would
 * just be recomputed on every render.
 */
export function useCachedContentsData(
    inputs: CachedContentDataInput[],
    options: CachedContentDataOptions
): Map<ContentDto['id'], CachedContentData> {
    const { locale, movieInclude = MOVIE_INCLUDE_LIST } = options;
    const cinemaId = options.cinema?.id;

    // Contents the caller did not hand in are fetched here, off the very cache entry
    // `useContent`/`useContentsByIds` use — so a caller that already batched them costs nothing.
    const missingContentIds = useMemo(
        () =>
            inputs
                .filter(isIdentified)
                .filter((input) => !input.content)
                .map((input) => input.contentId),
        [inputs]
    );

    const contentQueries = useQueries({
        queries: missingContentIds.map((contentId) => contentByIdQueryOptions(contentId))
    });

    const contentQueriesKey = contentQueries.map((query) => `${query.data?.id ?? 0}-${query.dataUpdatedAt}`).join(',');

    const resolvedInputs = useMemo(() => {
        const fetchedContents = new Map<ContentDto['id'], ContentDto>();
        for (const query of contentQueries) {
            const content = query.data as unknown as ContentDto | null;
            if (content?.id) fetchedContents.set(content.id, content);
        }
        return inputs
            .filter(isIdentified)
            .map((input) => ({ ...input, content: input.content ?? fetchedContents.get(input.contentId) ?? null }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputs, contentQueriesKey]);

    const movieIds = useMemo(
        () =>
            Array.from(
                new Set(
                    resolvedInputs
                        .map((input) => input.content?.movieId)
                        .filter((movieId): movieId is MovieDto['id'] => !!movieId)
                )
            ),
        [resolvedInputs]
    );

    const movieQueries = useQueries({
        queries: movieIds.map((movieId) => movieQueryOptions(movieId, locale, cinemaId, movieInclude))
    });

    const movieQueriesKey = movieQueries.map((query) => `${query.data?.id ?? 0}-${query.dataUpdatedAt}`).join(',');

    return useMemo(() => {
        const moviesById = new Map<MovieDto['id'], MovieDto>();
        for (const query of movieQueries) {
            const movie = query.data as MovieDto | null;
            if (movie?.id) moviesById.set(movie.id, movie);
        }

        return new Map(
            resolvedInputs.map((input) => [
                input.contentId,
                deriveCachedContentData(
                    { ...input, movie: input.content?.movieId ? moviesById.get(input.content.movieId) : null },
                    options
                )
            ])
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedInputs, movieQueriesKey, locale, options.countryCode, cinemaId, movieInclude]);
}

/** Single-content variant of {@link useCachedContentsData}. */
export function useCachedContentData(
    input: CachedContentDataInput,
    options: CachedContentDataOptions
): CachedContentData | undefined {
    // Memoized on the input's own fields, not on the object: callers build the input inline, so an
    // object identity dep would rebuild the list (and the derived map) on every render.
    const inputs = useMemo(
        () => [input],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [input.contentId, input.content, input.showing]
    );
    const dataById = useCachedContentsData(inputs, options);
    return input.contentId ? dataById.get(input.contentId) : undefined;
}
